import React, { useState, useEffect, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Páginas críticas (imports estáticos - necesarias para primer render)
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import MaintenancePage from './pages/MaintenancePage';

// Helper para lazy loading con manejo de errores de chunks faltantes
// Detecta cuando un chunk ya no existe (nuevo despliegue) y recarga la página
const lazyWithRetry = (
  importFn: () => Promise<{ default: React.ComponentType<any> }>
) => {
  return React.lazy(() =>
    importFn().catch((error) => {
      // Detectar si es un error de chunk faltante (404, ChunkLoadError, etc.)
      const isChunkError =
        error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Loading chunk') ||
        error.message?.includes('Failed to load') ||
        error.name === 'ChunkLoadError';

      if (isChunkError) {
        console.warn('[LazyLoad] Chunk not found, reloading to get latest version...');
        
        // Recargar la página para obtener los nuevos assets
        window.location.reload();
        
        // Retornar una promesa que nunca resuelve (la página se recargará)
        return new Promise(() => {});
      }

      // Re-lanzar otros errores para que ErrorBoundary los maneje
      throw error;
    })
  );
};

// Páginas con lazy loading (code splitting) - usando lazyWithRetry para auto-recuperación
const OTPPage = lazyWithRetry(() => import('./pages/OTPPage'));
const EmailCapturePage = lazyWithRetry(() => import('./pages/EmailCapturePage'));
const HomePage = lazyWithRetry(() => import('./pages/HomePage'));
const PaymentPage = lazyWithRetry(() => import('./pages/PaymentPage'));
const SuccessPage = lazyWithRetry(() => import('./pages/SuccessPage'));
const TransactionHistoryPage = lazyWithRetry(() => import('./pages/TransactionHistoryPage'));
const CompanyDataPage = lazyWithRetry(() => import('./pages/CompanyDataPage'));

import ThemeProvider from './utils/theme';
import { AuthProvider, useAuth } from './stores/authContext';
import { BrandConfigProvider, useBrandConfig } from './stores/brandConfigContext';
import { Toaster } from 'sonner';
import { FloatingSupportButton } from './components/floating-support-button';
import { usePageTracking } from './hooks/usePageTracking';
import ErrorBoundary from './components/ErrorBoundary';
import { OfflineBanner } from './components/OfflineBanner';
import { setCircuitBreakerCallbacks } from './utils/circuitBreaker';

import HomeSkeleton from './components/skeletons/HomeSkeleton';
import CompanyDataSkeleton from './components/skeletons/CompanyDataSkeleton';
import SuccessSkeleton from './components/skeletons/SuccessSkeleton';
import PaymentSkeleton from './components/skeletons/PaymentSkeleton';
import OTPSkeleton from './components/skeletons/OTPSkeleton';
import TransactionHistoryGridSkeleton from './components/skeletons/TransactionHistoryGridSkeleton';


// Wrappers con Suspense específico por página
const LazyOTPPage = () => (
  <Suspense fallback={<OTPSkeleton />}>
    <OTPPage />
  </Suspense>
);

const LazyEmailCapturePage = () => (
  <Suspense fallback={null}>
    <EmailCapturePage />
  </Suspense>
);

const LazyHomePage = () => (
  <Suspense fallback={<HomeSkeleton />}>
    <HomePage />
  </Suspense>
);

const LazyPaymentPage = () => (
  <Suspense fallback={<PaymentSkeleton />}>
    <PaymentPage />
  </Suspense>
);

const LazySuccessPage = () => (
  <Suspense fallback={<SuccessSkeleton />}>
    <SuccessPage />
  </Suspense>
);

const LazyTransactionHistoryPage = () => (
  <Suspense fallback={<TransactionHistoryGridSkeleton />}>
    <TransactionHistoryPage />
  </Suspense>
);

const LazyCompanyDataPage = () => (
  <Suspense fallback={<CompanyDataSkeleton />}>
    <CompanyDataPage />
  </Suspense>
);


// Mapa de skeletons por ruta para ProtectedRoute
const routeSkeletonMap: Record<string, React.ReactNode> = {
  'home': <HomeSkeleton />,
  'payment': <PaymentSkeleton />,
  'success': <SuccessSkeleton />,
  'company-data': <CompanyDataSkeleton />,
  'transaction-history': <TransactionHistoryGridSkeleton />,
};

// Componente para rutas protegidas
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { state, logout } = useAuth();
  const location = useLocation();
  
  // Extraer brandKeyword y ruta de la URL actual
  const brandKeyword = location.pathname.split('/')[1] || 'acme';
  const route = location.pathname.split('/')[2] || 'home';
  
  // CRÍTICO: Mostrar skeleton específico mientras se restaura la sesión
  // NO renderizar children para evitar que disparen requests sin token
  if (state.isLoading) {
    return routeSkeletonMap[route] || <HomeSkeleton />;
  }
  
  // Verificar si el token local ha expirado (sincronizado con expiresAt)
  // Esto captura casos donde el token expiró pero el estado aún marca isAuthenticated
  if (state.isAuthenticated && state.expiresAt && state.expiresAt <= Date.now()) {
    console.warn('[ProtectedRoute] Token expirado localmente, forzando logout');
    logout();
    return <Navigate to={`/${brandKeyword}`} replace />;
  }
  
  // Redirigir al login de la MISMA marca si no está autenticado
  if (!state.isAuthenticated) {
    return <Navigate to={`/${brandKeyword}`} replace />;
  }
  
  return <>{children}</>;
};

// Componente para redirect desde /
// Siempre redirige a ACME cuando la URL no tiene marca
const SmartHomeRedirect = () => {
  console.log('[SmartHomeRedirect] Redirecting to: acme (default)');
  return <Navigate to="/acme" replace />;
};


const AppRoutes = () => {
  const { isNotFound } = useBrandConfig();
  
  // Si hay un error 404, mostrar NotFoundPage
  if (isNotFound) {
    return <NotFoundPage />;
  }
  
  return (
    <Routes>
      {/* Ruta raíz redirige a la última marca usada o ACME por defecto */}
      <Route path="/" element={<SmartHomeRedirect />} />
      
      {/* Ruta explícita para página 404 */}
      <Route path="/404" element={<NotFoundPage />} />
      
      {/* Ruta explícita para página de mantenimiento (para pruebas) */}
      <Route path="/maintenance" element={<MaintenancePage onRetry={() => window.location.href = '/'} />} />
      
      
      {/* Todas las rutas usan brandKeyword */}
      <Route path="/:brandKeyword">
        <Route index element={<LoginPage />} />
        <Route path="otp" element={<LazyOTPPage />} />
        <Route path="complete-email" element={<LazyEmailCapturePage />} />
        <Route
          path="home"
          element={
            <ProtectedRoute>
              <LazyHomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="payment"
          element={
            <ProtectedRoute>
              <LazyPaymentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="success"
          element={
            <ProtectedRoute>
              <LazySuccessPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="transaction-history"
          element={
            <ProtectedRoute>
              <LazyTransactionHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="company-data"
          element={
            <ProtectedRoute>
              <LazyCompanyDataPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch-all muestra página 404 para rutas no existentes */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const AppContent = () => {
  const location = useLocation();
  const { isNotFound, config: brandConfig, loading: brandLoading } = useBrandConfig();
  const skeletonRemovedRef = React.useRef(false); // Flag para evitar re-ejecución
  
  // Google Tag Manager page tracking
  usePageTracking();
  
  // Efecto ÚNICO unificado para ocultar el skeleton HTML
  // Elimina condición de carrera entre dos efectos separados
  React.useEffect(() => {
    // Si ya fue removido, no hacer nada
    if (skeletonRemovedRef.current) return;
    
    const skeleton = document.getElementById('initial-skeleton');
    if (!skeleton) {
      skeletonRemovedRef.current = true;
      return;
    }
    
    const routesWithSkeleton = ['otp', 'home', 'payment', 'success', 'company-data', 'transaction-history'];
    const segments = location.pathname.split('/').filter(Boolean);
    const route = segments[1] || '';
    
    // Condición para ocultar:
    // 1. Si es una ruta con skeleton React propio → ocultar INMEDIATAMENTE
    // 2. Si NO es ruta con skeleton Y brandLoading terminó → ocultar
    const shouldHide = routesWithSkeleton.includes(route) || !brandLoading;
    
    if (shouldHide) {
      skeleton.classList.add('hidden');
      const timeoutId = setTimeout(() => {
        skeleton.remove();
        skeletonRemovedRef.current = true;
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, brandLoading]);
  
  // Efecto para actualizar el favicon dinámicamente via DOM
  // SOLO actualiza cuando el href actual es diferente al deseado
  // TAMBIÉN guarda en localStorage para futuras visitas
  React.useEffect(() => {
    // Solo proceder si no está cargando Y tenemos un icono de marca
    if (!brandLoading && brandConfig?.icono) {
      const faviconEl = document.getElementById('dynamic-favicon') as HTMLLinkElement;
      
      if (faviconEl) {
        const currentHref = faviconEl.href;
        const targetHref = brandConfig.icono;
        
        // Solo actualizar si el href actual es DIFERENTE al que queremos
        // Comparamos de forma flexible porque el browser puede tener URL absoluta
        const isAlreadyCorrect = currentHref === targetHref || 
                                 currentHref.endsWith(new URL(targetHref, window.location.origin).pathname);
        
        if (!isAlreadyCorrect) {
          faviconEl.type = 'image/png';  // Todos los iconos son PNG
          faviconEl.href = targetHref;
          console.log('[Favicon] Updated to:', targetHref);
        } else {
          console.log('[Favicon] Already correct, skipping update');
        }
        
        // Guardar en localStorage para futuras visitas
        const segments = location.pathname.split('/').filter(Boolean);
        const brand = segments[0] || 'acme';
        if (brand !== 'acme' && brand !== '404') {
          try {
            localStorage.setItem('favicon_' + brand, targetHref);
            console.log('[Favicon] Cached in localStorage for brand:', brand);
          } catch (e) {
            // localStorage no disponible, ignorar
          }
        }
      }
    }
  }, [brandLoading, brandConfig?.icono, location.pathname]);
  
  // Efecto para guardar el título en localStorage para futuras visitas
  React.useEffect(() => {
    if (!brandLoading && brandConfig?.nombre) {
      const segments = location.pathname.split('/').filter(Boolean);
      const brand = segments[0] || 'acme';
      
      if (brand !== 'acme' && brand !== '404') {
        const title = `Portal de Pagos ${brandConfig.nombre}`;
        try {
          localStorage.setItem('title_' + brand, title);
          console.log('[Title] Cached in localStorage for brand:', brand);
        } catch (e) {
          // localStorage no disponible, ignorar
        }
      }
    }
  }, [brandLoading, brandConfig?.nombre, location.pathname]);
  
  // Mientras carga la marca, retornar null
  // El skeleton de index.html (fuera del root) permanece visible
  if (brandLoading) {
    return null;
  }
  
  const isNotFoundPage = location.pathname === '/404' || isNotFound;
  
  // Solo mostrar el botón flotante si asistenteIA está habilitado
  const showFloatingButton = brandConfig?.asistenteIA === true;
  
  return (
    <>
      <Toaster position="top-center" richColors />
      <AppRoutes />
      {showFloatingButton && <FloatingSupportButton />}
    </>
  );
};

// Componente wrapper con Circuit Breaker
const AppWithCircuitBreaker = () => {
  const [showMaintenance, setShowMaintenance] = useState(false);
  
  useEffect(() => {
    setCircuitBreakerCallbacks(
      () => setShowMaintenance(true),   // onOpen
      () => setShowMaintenance(false)   // onClose
    );
  }, []);
  
  const handleRetry = () => {
    // Forzar recarga para verificar si el API se recuperó
    window.location.reload();
  };
  
  if (showMaintenance) {
    return <MaintenancePage onRetry={handleRetry} />;
  }
  
  return (
    <HelmetProvider>
      <BrowserRouter>
        <BrandConfigProvider>
          <ThemeProvider>
            <AuthProvider>
              <OfflineBanner />
              <ErrorBoundary>
                <AppContent />
              </ErrorBoundary>
            </AuthProvider>
          </ThemeProvider>
        </BrandConfigProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
};

// App principal
const App = () => {
  return <AppWithCircuitBreaker />;
};

export default App;